import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";

import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { DvsChartComponent } from "src/app/shared/dvs-chart/dvs-chart.component";
import { APIResults } from "src/app/sharedinterfaces";
import { AdjustmentHistoryComponent } from "src/app/shared/adjustment-history/adjustment-history.component";
import { RegimeListComponent } from "src/app/shared/regime-list/regime-list.component";

@Component({
	selector: "app-track-history",
	templateUrl: "./track-history.component.html",
	styleUrls: ["./track-history.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [DvsChartComponent, AdjustmentHistoryComponent, RegimeListComponent]
})
export class TrackHistoryComponent implements OnInit {
	api = inject(ApiService);
	private charting = inject(ChartingService);

	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	rotor: APIResults.Rotor;
	@Input()
	regimeConfigs: APIResults.RegimeIndex;

	public regimeCounts: APIResults.RegimeCount[];
	public selectedRegimes: number[];

	public timelineOptions: Highcharts.Options;

	public adjustments: APIResults.AdjustmentHistory[];

	async ngOnInit() {
		// TODO: this fails if no track available
		await this.updateAdjustments();
		await this.loadRegimes();
	}

	rotorChange() {
		this.ngOnInit();
	}

	// When first loaded or upstream params (sensor, order) change
	async loadRegimes() {
		const newCounts = await this.api.GetTrackHistoryCounts(this.asset.asset_id, this.rotor.rotorid);
		if (this.selectedRegimes) {
			// Remove regimes that are no longer present
			this.selectedRegimes = this.selectedRegimes.filter(regimeId => newCounts.find(item => item.regime_id === regimeId));
			// Carry over current selection
			for (const recheck of this.selectedRegimes) {
				const sameRegime = newCounts.find(item => item.regime_id === recheck);
				if (sameRegime) {
					sameRegime.evaluate = true;
				}
			}
		}
		// If we had none selected, or all were removed, select defaults
		if (!this.selectedRegimes || this.selectedRegimes.length === 0) {
			// Populate selection with default regimes
			let evalempty = true;
			for (const regime of newCounts) {
				const config = this.regimeConfigs[regime.regime_id];
				if (config) {
					regime.evaluate = config.default;
				}
				if (regime.evaluate) {
					evalempty = false;
				}
			}
			// If no default regimes available, select last one
			if (evalempty && newCounts.length) {
				newCounts[newCounts.length - 1].evaluate = true;
			}
		}
		this.regimeCounts = newCounts;
		await this.regimesChange();
	}

	async regimesChange() {
		this.selectedRegimes = this.regimeCounts.filter(item => item.evaluate).map(item => item.regime_id);
		await this.updatePlots();
	}

	async updatePlots() {
		this.timelineOptions = undefined;
		if (!this.selectedRegimes || !this.selectedRegimes.length) {
			return;
		}
		const results = await this.api.GetTrackHistory(this.asset.asset_id, this.rotor.rotorid, this.selectedRegimes);
		const timelineSeries: Highcharts.SeriesLineOptions[] = [];
		for (const regimeData of results) {
			let regimeLookup = this.regimeCounts.find(item => item.regime_id === regimeData.regime);
			if (!regimeLookup) regimeLookup = { name: "Unknown regime " + regimeData.regime, regime_id: regimeData.regime, count: 0, evaluate: false };
			const timelinePoints: any[] = [];
			for (const row of regimeData.rows) {
				timelinePoints.push({ x: row[0] * 1000, y: row[1] });
			}
			timelineSeries.push({
				name: regimeLookup.name,
				type: "line",
				data: timelinePoints,
			});
		}
		const trackUnits = this.rotor.inches ? "in" : "mm";
		this.timelineOptions = this.charting.RTBHistoryTimelineDefaults(timelineSeries, "Split (" + trackUnits + ")", this.fleet ? this.fleet.time_offset_hours : 0, this.adjustments, undefined, this);
	}

	async updateAdjustments() {
		const result = await this.api.GetAdjustmentHistory(this.asset.asset_id, this.rotor.rotorid);
		this.adjustments = result;
	}
}
